import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { CONTACT } from "../data/site";

const LINES = ["Fleet Maintenance.", "Anywhere. Anytime."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollToServices = () =>
    document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      {/* Parallax fleet photography */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src="/assets/banner.png" alt="Mobile Maintenance service fleet of trucks" className="w-full h-full object-cover object-[center_18%] grayscale contrast-125" />
      </motion.div>
      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-mm-base/50 via-mm-base/30 to-mm-base" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-mm-base/70 via-mm-base/20 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-mm-green/[0.04] mix-blend-overlay" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-[1400px] w-full px-5 sm:px-8 pb-20 sm:pb-28 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-2 w-2 rounded-full bg-mm-green animate-pulse" />
          <span className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase text-mm-green">
            24 / 7 / 365 • Serving Nationwide
          </span>
        </motion.div>

        <h1 className="font-heading font-black uppercase leading-[1.05] tracking-tighter text-white text-[13vw] sm:text-[10vw] lg:text-[7vw]">
          {LINES.map((line, i) => (
            <motion.span
              key={line}
              className={`block ${i === 1 ? "text-white" : ""}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-7 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <p className="max-w-xl text-gray-300 text-base lg:text-lg leading-relaxed">
            We bring the shop to your fleet. On-site repair, preventative maintenance,
            and total fleet care that keeps your equipment earning, not waiting.
          </p>
          <div className="flex flex-wrap gap-4 shrink-0">
            <button
              onClick={() => document.querySelector("#request")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-request-btn"
              className="group font-heading font-bold uppercase tracking-wider text-lg bg-white text-black px-7 py-4 hover:bg-mm-green transition-colors duration-200"
            >
              Request Priority Service
            </button>
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wider text-lg border border-mm-green/40 text-white px-7 py-4 hover:border-mm-green hover:text-mm-green transition-colors duration-200"
            >
              <Phone size={18} /> Call Now
            </a>
          </div>
        </motion.div>
      </motion.div>

      <button
        onClick={scrollToServices}
        className="relative z-10 mx-auto mb-6 text-mm-green/70 hover:text-mm-green transition-colors"
        aria-label="Scroll down"
      >
        <ArrowDown className="animate-bounce" />
      </button>
    </section>
  );
}
